import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Nachricht gesendet. Wir melden uns zeitnah.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12 md:py-20">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4 animate-fade-up">
            <span className="divider-thin" />Kontakt
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-6xl tracking-tight text-slate-900 leading-[1.05] animate-fade-up delay-100">
            Sprechen wir <span className="font-serif italic font-medium">miteinander</span>.
          </h1>
          <p className="text-slate-600 mt-4 max-w-2xl animate-fade-up delay-200">
            Persönliche Beratung — direkt vom Inhaber. Schreiben Sie uns oder rufen Sie an.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20 grid md:grid-cols-5 gap-12">
        <div className="md:col-span-2 space-y-8">
          <InfoBlock icon={Phone} title="Telefon" lines={[<a key="t" href="tel:+436677608726" className="hover:text-red-600 transition-crisp">+43 667 760 8726</a>]} />
          <InfoBlock icon={Mail} title="E-Mail" lines={[<a key="e" href="mailto:kontakt@dr-automobile.at" className="hover:text-red-600 transition-crisp">kontakt@dr-automobile.at</a>]} />
          <InfoBlock icon={MapPin} title="Adresse" lines={["Ringgasse 6", "4840 Vöcklabruck", "Österreich"]} />
          <InfoBlock icon={Clock} title="Öffnungszeiten" lines={["Mo–Fr: 09:00 – 18:00", "Sa: 09:00 – 13:00", "Termine nach Vereinbarung"]} />
        </div>

        <form onSubmit={submit} className="md:col-span-3 border border-slate-200 p-6 md:p-10 space-y-5" data-testid="contact-form">
          <h2 className="font-heading font-bold text-2xl text-slate-900">Ihre Nachricht</h2>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="contact-name" />
            <Field label="E-Mail *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="contact-email" />
          </div>
          <Field label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="contact-phone" />

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2 block">Nachricht *</label>
            <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message"
              className="w-full border border-slate-200 px-3 py-3 text-sm focus:outline-none focus:border-slate-900 resize-none" />
          </div>

          <button type="submit" disabled={sending} data-testid="contact-submit" className="bg-slate-900 text-white px-8 py-3.5 text-sm font-medium hover:bg-red-600 transition-crisp disabled:opacity-50">
            {sending ? "Sende…" : "Nachricht senden"}
          </button>
        </form>
      </div>
    </div>
  );
}

function InfoBlock({ icon: Icon, title, lines }) {
  return (
    <div className="border-l-2 border-slate-900 pl-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className="text-slate-900" />
        <h3 className="font-heading font-semibold text-sm uppercase tracking-widest">{title}</h3>
      </div>
      <div className="text-slate-700 text-sm space-y-1">{lines.map((l, i) => <div key={i}>{l}</div>)}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2 block">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full border border-slate-200 px-3 py-3 text-sm focus:outline-none focus:border-slate-900" />
    </div>
  );
}
